import { useEffect, useRef, useState } from 'react';

const CHART_HEIGHT = 168;
const PERFORMANCE_PERIODS = [
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
];

function buildYAxisLabels(maxMinutes) {
  const step = maxMinutes / 3;
  return [maxMinutes, step * 2, step, 0].map((value) => {
    if (value <= 0) return '0 min';
    return `${Math.round(value)} min`;
  });
}

function PeriodDropdown({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const selected = PERFORMANCE_PERIODS.find((period) => period.value === value);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-2 rounded-lg bg-[#6E4F99] px-4 py-2 text-[11px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
      >
        {selected?.label || 'This Week'}
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-2 min-w-[140px] overflow-hidden rounded-lg border border-[#E7E7E7] bg-white shadow-lg">
          {PERFORMANCE_PERIODS.map((period) => (
            <button
              key={period.value}
              type="button"
              onClick={() => {
                onChange(period.value);
                setOpen(false);
              }}
              className={`block w-full px-4 py-2 text-left text-sm transition hover:bg-[#F5F5F5] ${
                period.value === value ? 'font-semibold text-[#6E4F99]' : 'text-[#666666]'
              }`}
            >
              {period.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value, delta1, delta2, icon }) {
  return (
    <article className="flex flex-1 flex-col rounded-2xl border border-[#1A87CC]/35 bg-white px-5 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-base font-medium text-[#1B1B1B]">{title}</p>
        <div className="shrink-0 text-[#1A87CC]">{icon}</div>
      </div>
      <p className="mt-1 text-[32px] font-semibold leading-none text-[#146599]">{value}</p>
      <p className="mt-2 text-[20px] font-semibold text-[#9369CC]">
        {delta1} <span className="text-sm font-medium">{delta2}</span>
      </p>
    </article>
  );
}

function ProgressChart({ chartItems, maxMinutes, loading }) {
  const yAxisLabels = buildYAxisLabels(maxMinutes);

  if (loading) {
    return <div className="mt-6 h-[190px] animate-pulse rounded-xl bg-[#F3F3F3]" />;
  }

  if (!chartItems.length) {
    return (
      <p className="mt-6 flex h-[190px] items-center justify-center text-sm text-[#666666]">
        No performance data for this period yet.
      </p>
    );
  }

  return (
    <div className="mt-6 flex gap-3">
      <div
        className="flex flex-col justify-between pb-[22px] text-[11px] font-medium text-[#999999]"
        style={{ height: CHART_HEIGHT + 22 }}
      >
        {yAxisLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div className="relative flex-1">
        <div className="relative" style={{ height: CHART_HEIGHT }}>
          {[0, 1, 2, 3].map((line) => (
            <div
              key={line}
              className="pointer-events-none absolute left-0 right-0 border-t border-dashed border-[#D9D9D9]"
              style={{ bottom: `${(line / 3) * 100}%` }}
            />
          ))}

          <div className="absolute bottom-0 left-0 top-0 w-px bg-[#C4B5E8]" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-[#C4B5E8]" />

          <div className="absolute inset-0 flex items-end justify-between gap-3 px-1">
            {chartItems.map((item) => {
              const heightPercent = Math.max((item.minutes / maxMinutes) * 100, 6);
              return (
                <div key={item.id} className="flex h-full flex-1 flex-col items-center justify-end">
                  <div
                    className="w-full max-w-[52px]"
                    style={{ height: `${heightPercent}%`, backgroundColor: item.color }}
                    title={`${item.minutes} min`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-2 flex justify-between gap-2 pl-1 pr-1">
          {chartItems.map((item) => (
            <span
              key={`${item.id}-label`}
              className="flex-1 text-center text-[11px] font-medium text-[#1B1B1B] sm:text-xs"
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProgressOverviewSection({
  categories = [],
  selectedCategoryId,
  onCategoryChange,
  isCategorySubscribed,
  performancePeriod,
  onPerformancePeriodChange,
  overview,
  loading = false,
  progressDisabled = false,
}) {
  const stats = overview?.stats;
  const chartItems = overview?.chartItems || [];
  const maxMinutes = overview?.maxMinutes || 120;

  return (
    <section className="flex w-[900px] flex-row justify-between">
      <article className="h-[314px] w-[598px] rounded-2xl bg-white p-5 shadow-[0_2px_14px_rgba(0,0,0,0.06)]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-[#111216]">Your Progress</h2>
            <p className="mt-1 text-[11px] font-medium text-[#7D7F81]">Track your progress completion over time.</p>
          </div>
          <PeriodDropdown
            value={performancePeriod}
            onChange={onPerformancePeriodChange}
            disabled={loading || progressDisabled}
          />
        </div>

        <ProgressChart chartItems={chartItems} maxMinutes={maxMinutes} loading={loading} />
      </article>

      <div className="flex h-[316px] w-[289px] flex-col gap-3">
        <div className="flex rounded-xl bg-white p-1 shadow-sm">
          {categories.map((category) => {
            const isSubscribed = isCategorySubscribed?.(category.id) ?? true;
            const isSelected = String(selectedCategoryId) === String(category.id);

            return (
              <button
                key={category.id}
                type="button"
                onClick={() => onCategoryChange(category.id)}
                className={`flex-1 rounded-xl px-4 py-2 text-base font-semibold transition ${
                  isSelected && isSubscribed
                    ? 'bg-[#009DA8] text-white'
                    : 'text-[#666666] hover:text-[#1B1B1B]'
                }`}
              >
                {category.name}
              </button>
            );
          })}
        </div>

        {loading ? (
          <>
            <div className="h-[118px] animate-pulse rounded-2xl bg-white" />
            <div className="h-[118px] animate-pulse rounded-2xl bg-white" />
          </>
        ) : (
          <>
            <StatCard
              title="Total Study Hours"
              value={`${stats?.studyHours ?? 0} Hrs`}
              delta1={stats?.studyHoursDelta1 || '0 Hrs'}
              delta2={stats?.studyHoursDelta2 || 'From this week'}
              icon={
                <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />

            <StatCard
              title="Chapter Completed"
              value={String(stats?.chaptersCompleted ?? 0)}
              delta1={stats?.chaptersDelta1 || '0+'}
              delta2={stats?.chaptersDelta2 || 'From this week'}
              icon={
                <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="12" cy="12" r="10" stroke="#1A87CC" strokeWidth="1.8" />
                  <path
                    d="M8 12.5l2.5 2.5L16 9.5"
                    stroke="#1A87CC"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </>
        )}
      </div>
    </section>
  );
}
